/**
 * La file des dépenses à placer : relier un virement émis à ce qu'il solde.
 *
 * ## Pourquoi une file distincte de celle des paiements, et non une file unifiée
 *
 * Parce que **les gestes ne sont pas les mêmes**. Sur une entrée, la question
 * est « quelle facture ? » et l'action est une affectation **annulable**. Sur
 * une dépense, **l'argent est parti** : il n'y a pas de retour. Une file
 * unifiée mettrait un bouton « Défaire » à côté d'un mouvement irréversible —
 * exactement la confusion que le journal refuse déjà en n'offrant pas
 * « Défaire » sur les dépenses.
 *
 * ## Il n'applique rien tout seul
 *
 * Le moteur a déjà placé ce dont le montant coïncidait ; ce qui arrive ici est
 * précisément ce qu'il n'a pas voulu trancher. Chaque ligne attend donc une
 * décision, et le bouton la nomme.
 *
 * ## Le cas rare, et pourquoi il a sa place ici
 *
 * Un virement peut être un vrai remboursement de note de crédit **sans porter
 * aucune annotation** — l'administration a oublié d'écrire le numéro. C'est
 * rare, ce n'est pas impossible. La recherche par numéro est donc offerte sur
 * TOUTE ligne de la file, y compris celles que le moteur a classées « aucune
 * référence ». Sans ce geste, ces virements resteraient dans la file pour
 * toujours.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  AlertTriangle, Ban, CheckSquare, ChevronDown, ChevronLeft, ChevronRight, Link2, Loader2,
  RotateCcw, Search, Square, Users,
} from 'lucide-react';
import clsx from 'clsx';
import toast from 'react-hot-toast';
import {
  chercherNotesARembourser,
  classerHorsPerimetre,
  classerPlusieursHorsPerimetre,
  countDepensesAPlacer,
  lireLesComptesDesFamilles,
  defairePlacement,
  loadDepensesAPlacer,
  loadPlacements,
  placerRemboursement,
  type DepenseAPlacer,
  type NoteARembourser,
  type PlacementExistant,
} from '../../../lib/payments/refundActions.supabase';
import {
  classerLaSortie, referencesDuVirement, type Nature, type NatureDeLaSortie,
} from '../../../lib/payments/sortie';
import {
  lireLeCompte, type CompteDeFamille, type LectureDuCompte,
} from '../../../lib/payments/compteDuBeneficiaire';
import NotesCitees from './NotesCitees';
import {
  avertissementAvantRemboursement,
  LIBELLES_DE_DESTINEE,
} from '../../../lib/creditNotes/destinee';
import { messageErreur } from '../../../lib/payments/erreur';
import { lireLesJumeaux } from '../../../lib/payments/jumeaux.supabase';
import type { JumeauEnFile } from '../../../lib/payments/queueReason';
import VirementLuDeuxFois from './VirementLuDeuxFois';
import { dateCourte, euros } from './money';

export default function DepensesAPlacer({
  rafraichir,
  onCount,
}: {
  rafraichir: number;
  onCount?: (nombre: number) => void;
}) {
  const [lignes, setLignes] = useState<DepenseAPlacer[]>([]);
  const [chargement, setChargement] = useState(true);
  const [erreur, setErreur] = useState<string | null>(null);
  const [deplie, setDeplie] = useState<string | null>(null);
  /* La nature affichée. `null` = toutes. */
  const [filtre, setFiltre] = useState<NatureDeLaSortie | null>(null);
  const [selection, setSelection] = useState<Set<string>>(new Set());
  const [lot, setLot] = useState(false);
  const [page, setPage] = useState(1);
  /* Les comptes reconnus, chargés UNE fois pour toute la file. La vue agrège
     17 717 encaissements : une requête par ligne coûterait cinquante fois le
     prix d'une. */
  const [comptesConnus, setComptesConnus] = useState<CompteDeFamille[]>([]);
  const [jumeaux, setJumeaux] = useState<Map<string, JumeauEnFile>>(new Map());

  const charger = useCallback(async () => {
    setChargement(true);
    try {
      /*
        La file ENTIÈRE, et c'est ce qui permet de la trier par nature.

        590 mouvements au 19 septembre 2026 : les charger tous coûte une
        requête et rend les comptes par nature EXACTS. Compter sur une page de
        cent aurait annoncé « 7 remboursements » sur 44 — un chiffre qu'on lit
        comme une mesure. Le jour où cette file dépassera le millier, il faudra
        compter en base ; l'écran le dit alors plutôt que de tronquer en
        silence.
      */
      const [file, compte] = await Promise.all([
        loadDepensesAPlacer({ limite: 1000 }),
        countDepensesAPlacer(),
      ]);
      setLignes(file);
      setSelection(new Set());
      setPage(1);

      /*
        Le compte bancaire dit la famille. 1 409 comptes en désignent une sans
        ambiguïté, et c'est souvent le seul indice quand la communication n'en
        donne aucun.

        Chargé après la file, et sans la bloquer : un échec ici ne doit pas
        empêcher de traiter la file — l'écran affichera l'IBAN sans le nom.
      */
      const ibans = file.map((d) => d.counterpartyIban).filter(Boolean) as string[];
      try {
        setComptesConnus(await lireLesComptesDesFamilles(ibans));
      } catch {
        setComptesConnus([]);
      }
      /* La même sortie lue dans le relevé ET dans l'export CSV (27/09/2026) :
         signalée, pour qu'on la réunisse au lieu de la placer. Sans bloquer la
         file si la lecture échoue. */
      try {
        setJumeaux(await lireLesJumeaux('sortie', file.map((d) => ({
          id: d.id, valueDate: d.valueDate, amountCents: d.amountCents,
        }))));
      } catch {
        setJumeaux(new Map());
      }
      setErreur(null);
      onCount?.(compte.nombre);
    } catch (e) {
      setErreur(messageErreur(e, 'Impossible de charger la file des dépenses'));
    } finally {
      setChargement(false);
    }
  }, [onCount]);

  useEffect(() => { void charger(); }, [charger, rafraichir]);

  /* Le wrapper de tout geste : il montre l'erreur de la base TELLE QU'ELLE EST.
     Les invariants 1' et 2' remontent par ce chemin, et les avaler reviendrait
     à laisser croire qu'un placement refusé a eu lieu. */
  const agir = useCallback(async (quoi: string, action: () => Promise<void>) => {
    try {
      await action();
      toast.success(quoi);
      await charger();
    } catch (e) {
      toast.error(messageErreur(e, "L'écriture a été refusée"));
    }
  }, [charger]);

  /*
    La nature de chaque ligne, calculée UNE fois. `classerLaSortie` est pure et
    éprouvée : la même règle vaut pour l'écran, pour les comptes et pour les
    paquets, donc aucun des trois ne peut diverger des autres.
  */
  /* Le compte de chaque ligne, lu UNE fois. La règle est pure et éprouvée :
     le même compte se lit pareil ici, dans le détail et dans le journal. */
  const lecturesDuCompte = useMemo(
    () => new Map(lignes.map((d) => [d.id, lireLeCompte(d, comptesConnus)])),
    [lignes, comptesConnus],
  );

  const natures = useMemo(
    () => new Map(lignes.map((d) => [d.id, classerLaSortie({
      communicationRaw: d.communicationRaw,
      freeText: d.freeText,
      counterpartyName: d.counterpartyName,
    })])),
    [lignes],
  );

  const comptes = useMemo(() => {
    const parNature = new Map<NatureDeLaSortie, { label: string; nb: number; cents: number }>();
    for (const d of lignes) {
      const n = natures.get(d.id)!;
      const e = parNature.get(n.code) ?? { label: n.label, nb: 0, cents: 0 };
      e.nb += 1;
      e.cents += d.unallocatedCents;
      parNature.set(n.code, e);
    }
    /* Les remboursements en tête : ce sont les seuls qui appellent un vrai
       rapprochement, et ils sont noyés — 44 sur 590. */
    const ordre: NatureDeLaSortie[] = [
      'remboursement_famille', 'volontariat', 'salaires', 'charges', 'subvention', 'fonctionnement',
    ];
    return [
      {
        code: null,
        label: 'Toutes',
        nb: lignes.length,
        cents: lignes.reduce((s, d) => s + d.unallocatedCents, 0),
      },
      ...ordre
        .filter((c) => parNature.has(c))
        .map((c) => ({ code: c, ...parNature.get(c)! })),
    ] as { code: NatureDeLaSortie | null; label: string; nb: number; cents: number }[];
  }, [lignes, natures]);

  const affichees = useMemo(
    () => (filtre === null ? lignes : lignes.filter((d) => natures.get(d.id)?.code === filtre)),
    [lignes, natures, filtre],
  );

  /*
    La file entière est CHARGÉE — c'est ce qui rend les comptes par nature
    exacts — mais elle n'est plus AFFICHÉE d'un bloc. 455 lignes dans une seule
    page rendaient l'écran lourd et la sélection illisible.

    Cinquante par page, et le compteur de sélection reste celui de la sélection
    ENTIÈRE, pas de la page : sélectionner 311 dépenses puis changer de page ne
    doit pas donner l'impression d'en avoir perdu 261.
  */
  const PAR_PAGE = 50;
  const pages = Math.max(1, Math.ceil(affichees.length / PAR_PAGE));
  const pageSure = Math.min(page, pages);
  const visibles = useMemo(
    () => affichees.slice((pageSure - 1) * PAR_PAGE, pageSure * PAR_PAGE),
    [affichees, pageSure],
  );

  /* Combien de CE filtre sont déjà cochées : c'est ce que la case d'en-tête
     doit refléter, sans quoi elle ment dès qu'on a coché à la main. */
  const cocheesDuFiltre = affichees.filter((d) => selection.has(d.id)).length;
  const toutCoche = affichees.length > 0 && cocheesDuFiltre === affichees.length;

  const cocherTout = () => {
    const s = new Set(selection);
    if (toutCoche) for (const d of affichees) s.delete(d.id);
    else for (const d of affichees) s.add(d.id);
    setSelection(s);
  };

  const cocherLaPage = () => {
    const s = new Set(selection);
    const toutesCochees = visibles.every((d) => s.has(d.id));
    if (toutesCochees) for (const d of visibles) s.delete(d.id);
    else for (const d of visibles) s.add(d.id);
    setSelection(s);
  };

  const beneficiaires = useMemo(() => {
    const parNom = new Map<string, { nom: string; ids: string[]; cents: number }>();
    for (const d of affichees) {
      const nom = d.counterpartyName ?? 'Bénéficiaire inconnu';
      const e = parNom.get(nom) ?? { nom, ids: [], cents: 0 };
      e.ids.push(d.id);
      e.cents += d.unallocatedCents;
      parNom.set(nom, e);
    }
    return [...parNom.values()].sort((a, b) => b.ids.length - a.ids.length || b.cents - a.cents);
  }, [affichees]);

  const totalSelection = lignes
    .filter((d) => selection.has(d.id))
    .reduce((s, d) => s + d.unallocatedCents, 0);

  const basculerSelection = (id: string) => {
    const s = new Set(selection);
    if (s.has(id)) s.delete(id); else s.add(id);
    setSelection(s);
  };

  /*
    Le motif écrit sur chaque ligne écartée est celui de sa NATURE, pas un
    libellé unique. Six mois plus tard, « Volontariat » explique le classement ;
    « hors périmètre » ne dit rien de plus que la colonne `target`.
  */
  const ecarterLaSelection = async () => {
    setLot(true);
    try {
      /*
        Un appel par NATURE, et non un seul avec un libellé moyen.

        Tant qu'on cochait ligne à ligne, une sélection portait presque
        toujours sur une seule nature. Depuis qu'un clic en coche 311, une
        sélection mélangée est le cas ORDINAIRE — et écrire « Dépense de
        fonctionnement » sur du volontariat et des salaires effacerait
        justement ce que la nature savait.
      */
      const parMotif = new Map<string, string[]>();
      for (const id of selection) {
        const motif = natures.get(id)?.label ?? 'Dépense de fonctionnement';
        parMotif.set(motif, [...(parMotif.get(motif) ?? []), id]);
      }

      let total = 0;
      for (const [motif, ids] of parMotif) {
        total += await classerPlusieursHorsPerimetre(ids, motif);
      }
      toast.success(`${total} dépense(s) écartée(s) du périmètre des familles`);
      await charger();
    } catch (e) {
      toast.error(messageErreur(e, 'Classement refusé'));
    } finally {
      setLot(false);
    }
  };

  if (chargement && lignes.length === 0) {
    return (
      <p className="flex items-center gap-2 text-sm text-gray-600">
        <Loader2 className="h-4 w-4 animate-spin" /> Chargement de la file…
      </p>
    );
  }

  return (
    <div className="space-y-4">
      <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
        <p className="font-medium">L'argent de ces virements est déjà parti.</p>
        <p className="mt-1">
          Les placer ne déplace pas d'argent : cela dit à quelle note de crédit ou à quelle
          facture ils correspondent. Cette file mélange deux populations qui n'ont rien à
          voir : les <strong>remboursements aux familles</strong>, qui se rapprochent d'une
          note de crédit, et les <strong>dépenses de fonctionnement</strong> — ONSS, salaires,
          fournisseurs, volontariat, charges — qui n'ont aucune contrepartie ici et attendent
          la comptabilité en partie double. Les onglets ci-dessous les séparent ; les secondes
          s'écartent par paquets.
        </p>
      </div>

      {erreur && (
        <p className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800">{erreur}</p>
      )}

      {lignes.length === 0 ? (
        <p className="rounded-md border border-gray-200 bg-gray-50 p-4 text-sm text-gray-600">
          Rien à placer. Toutes les dépenses lues sont rattachées à une pièce ou classées.
        </p>
      ) : (
        <>
          <ParNature
            comptes={comptes}
            filtre={filtre}
            onFiltrer={(n) => { setFiltre(n); setSelection(new Set()); setPage(1); }}
          />

          {filtre !== null && filtre !== 'remboursement_famille' && beneficiaires.length > 1 && (
            <ParBeneficiaire
              groupes={beneficiaires}
              onSelectionner={(ids) => setSelection(new Set([...selection, ...ids]))}
            />
          )}

          {/*
            Cocher d'un geste. Tant que la sélection se faisait ligne à ligne,
            écarter 311 dépenses de fonctionnement demandait 311 clics.

            Les deux portées sont distinctes et nommées : « les 311 de ce
            filtre » et « cette page ». Un seul bouton qui ferait l'un en
            laissant croire l'autre serait pire que deux.
          */}
          {affichees.length > 0 && (
            <div className="flex flex-wrap items-center gap-3 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm">
              <button
                onClick={cocherTout}
                disabled={lot}
                className="inline-flex items-center gap-2 font-medium text-gray-800 hover:text-indigo-700 disabled:opacity-50"
              >
                {toutCoche ? <CheckSquare className="h-4 w-4 text-indigo-600" />
                           : <Square className="h-4 w-4 text-gray-400" />}
                {toutCoche ? 'Tout décocher' : `Tout cocher — les ${affichees.length} de ce filtre`}
              </button>
              {pages > 1 && (
                <button
                  onClick={cocherLaPage}
                  disabled={lot}
                  className="text-gray-600 underline decoration-dotted hover:decoration-solid disabled:opacity-50"
                >
                  cette page seulement ({visibles.length})
                </button>
              )}
              {cocheesDuFiltre > 0 && !toutCoche && (
                <span className="text-gray-500 tabular-nums">
                  {cocheesDuFiltre} sur {affichees.length} cochée(s)
                </span>
              )}
            </div>
          )}

          {selection.size > 0 && (
            <div className="sticky top-2 z-10 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-indigo-300 bg-indigo-50 p-3 shadow-sm">
              <p className="text-sm text-indigo-900">
                <span className="font-medium">{selection.size} sélectionné(s)</span>,
                <span className="ml-1 tabular-nums">{euros(totalSelection)}</span>
                {/* La sélection survit au changement de page : le dire, sinon
                    « 311 » devant 50 lignes ressemble à une erreur. */}
                {selection.size > visibles.length && (
                  <span className="ml-1 text-indigo-700">sur l'ensemble du filtre</span>
                )}
              </p>
              <div className="flex gap-2">
                <button
                  onClick={() => setSelection(new Set())}
                  disabled={lot}
                  className="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                >
                  Tout désélectionner
                </button>
                <button
                  onClick={() => void ecarterLaSelection()}
                  disabled={lot}
                  className="inline-flex items-center gap-1.5 rounded-md bg-indigo-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50"
                >
                  {lot ? <Loader2 className="h-4 w-4 animate-spin" /> : <Ban className="h-4 w-4" />}
                  Écarter la sélection
                </button>
              </div>
            </div>
          )}

          {affichees.length === 0 ? (
            <p className="rounded-md border border-gray-200 bg-gray-50 p-4 text-sm text-gray-600">
              Aucune dépense de cette nature n'attend.
            </p>
          ) : (
            <>
              <ul className="divide-y divide-gray-200 rounded-md border border-gray-200">
                {visibles.map((d) => (
                  <LigneDeDepense
                    key={d.id}
                    depense={d}
                    nature={natures.get(d.id)!}
                    compte={lecturesDuCompte.get(d.id)!}
                    selectionnee={selection.has(d.id)}
                    onSelection={() => basculerSelection(d.id)}
                    ouverte={deplie === d.id}
                    onBasculer={() => setDeplie(deplie === d.id ? null : d.id)}
                    onAgir={agir}
                    jumeau={jumeaux.get(d.id) ?? null}
                  />
                ))}
              </ul>

              {pages > 1 && (
                <div className="flex items-center justify-between gap-3 text-sm">
                  <span className="text-gray-600 tabular-nums">
                    {(pageSure - 1) * PAR_PAGE + 1}–{(pageSure - 1) * PAR_PAGE + visibles.length}
                    {' '}sur {affichees.length}
                  </span>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => { setPage(pageSure - 1); setDeplie(null); }}
                      disabled={pageSure <= 1}
                      className="inline-flex items-center gap-1 rounded-md border border-gray-300 bg-white px-2.5 py-1.5 font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-40"
                    >
                      <ChevronLeft className="h-4 w-4" /> Précédente
                    </button>
                    <span className="tabular-nums text-gray-600">
                      page {pageSure} sur {pages}
                    </span>
                    <button
                      onClick={() => { setPage(pageSure + 1); setDeplie(null); }}
                      disabled={pageSure >= pages}
                      className="inline-flex items-center gap-1 rounded-md border border-gray-300 bg-white px-2.5 py-1.5 font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-40"
                    >
                      Suivante <ChevronRight className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              )}
            </>
          )}

          {lignes.length >= 1000 && (
            <p className="text-xs text-amber-800">
              Cette file dépasse le millier de lignes : les comptes par nature ci-dessus ne
              portent que sur les mille premières. Rien n'est tronqué en silence — cette
              phrase est là pour le dire.
            </p>
          )}
        </>
      )}
    </div>
  );
}

/** Les natures, avec leur compte et leur montant. Cliquer filtre la liste. */
function ParNature({
  comptes, filtre, onFiltrer,
}: {
  comptes: { code: NatureDeLaSortie | null; label: string; nb: number; cents: number }[];
  filtre: NatureDeLaSortie | null;
  onFiltrer: (n: NatureDeLaSortie | null) => void;
}) {
  return (
    <div className="flex flex-wrap gap-2">
      {comptes.map((c) => (
        <button
          key={c.code ?? 'toutes'}
          onClick={() => onFiltrer(c.code)}
          className={clsx(
            'rounded-md border px-3 py-1.5 text-sm',
            filtre === c.code
              ? 'border-indigo-500 bg-indigo-600 text-white'
              : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50',
            c.code === 'remboursement_famille' && filtre !== c.code && 'border-green-300 bg-green-50 text-green-900',
          )}
        >
          {c.label}
          <span className={clsx('ml-1.5 tabular-nums', filtre === c.code ? 'text-indigo-100' : 'text-gray-500')}>
            {c.nb} · {euros(c.cents)}
          </span>
        </button>
      ))}
    </div>
  );
}

/**
 * Les bénéficiaires d'une nature, pour les écarter par paquets.
 *
 * Demandé le 19 septembre 2026 : « regrouper tous les mouvements de volontariat
 * et de salaire et pouvoir les écarter d'un coup ». Le regroupement se fait par
 * BÉNÉFICIAIRE plutôt que par catégorie devinée : « Office National de Securite
 * Sociale », c'est 27 virements et 129 985,44 € qu'on reconnaît d'un coup d'œil
 * et qu'aucune règle n'a besoin d'interpréter.
 */
function ParBeneficiaire({
  groupes, onSelectionner,
}: {
  groupes: { nom: string; ids: string[]; cents: number }[];
  onSelectionner: (ids: string[]) => void;
}) {
  return (
    <div className="rounded-md border border-gray-200 bg-gray-50 p-3">
      <p className="flex items-center gap-1.5 text-xs font-medium uppercase tracking-wide text-gray-500">
        <Users className="h-3.5 w-3.5" /> Par bénéficiaire
      </p>
      <div className="mt-2 flex flex-wrap gap-2">
        {groupes.map((g) => (
          <button
            key={g.nom}
            onClick={() => onSelectionner(g.ids)}
            className="rounded border border-gray-300 bg-white px-2 py-1 text-xs text-gray-700 hover:bg-indigo-50 hover:border-indigo-300"
            title={`Sélectionner les ${g.ids.length} virements vers ${g.nom}`}
          >
            {g.nom}
            <span className="ml-1.5 tabular-nums text-gray-500">
              {g.ids.length} · {euros(g.cents)}
            </span>
          </button>
        ))}
      </div>
    </div>
  );
}

function LigneDeDepense({
  depense, nature, compte, selectionnee, onSelection, ouverte, onBasculer, onAgir, jumeau,
}: {
  depense: DepenseAPlacer;
  nature: Nature;
  compte: LectureDuCompte;
  selectionnee: boolean;
  onSelection: () => void;
  ouverte: boolean;
  onBasculer: () => void;
  onAgir: (quoi: string, action: () => Promise<void>) => Promise<void>;
  jumeau: JumeauEnFile | null;
}) {
  return (
    <li className={clsx('bg-white', selectionnee && 'bg-indigo-50/60')}>
      <div className="flex items-start gap-3 px-4 pt-3">
        <input
          type="checkbox"
          checked={selectionnee}
          onChange={onSelection}
          aria-label={`Sélectionner le virement de ${euros(depense.unallocatedCents)}`}
          className="mt-1 h-4 w-4 shrink-0 rounded border-gray-300 text-indigo-600"
        />
        <span className={clsx(
          'rounded-full px-2 py-0.5 text-xs font-medium',
          nature.famille ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-700',
        )}>
          {nature.label}
        </span>
        {nature.matched && (
          <span className="truncate text-xs text-gray-500" title="Ce qui l'a fait reconnaître">
            reconnu par « {nature.matched} »
          </span>
        )}
        {jumeau && (
          <span className="rounded-full bg-red-100 px-2 py-0.5 text-xs font-medium text-red-800">
            {jumeau.communications === 'differentes' ? 'lu deux fois ?' : 'lu deux fois'}
          </span>
        )}
      </div>
      <button
        onClick={onBasculer}
        className="flex w-full items-start gap-3 px-4 pb-3 pt-2 text-left hover:bg-gray-50"
      >
        {ouverte
          ? <ChevronDown className="mt-0.5 h-4 w-4 shrink-0 text-gray-400" />
          : <ChevronRight className="mt-0.5 h-4 w-4 shrink-0 text-gray-400" />}

        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
            <span className="font-medium tabular-nums text-gray-900">
              {euros(depense.unallocatedCents)}
            </span>
            {depense.unallocatedCents !== depense.amountCents && (
              <span className="text-xs text-gray-500">
                sur {euros(depense.amountCents)} virés
              </span>
            )}
            <span className="text-sm text-gray-600">{dateCourte(depense.valueDate)}</span>
            {depense.state === 'partial' && (
              <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800">
                partiellement placé
              </span>
            )}
            {depense.possiblyTruncated && (
              <span
                className="inline-flex items-center gap-1 rounded-full bg-orange-100 px-2 py-0.5 text-xs font-medium text-orange-800"
                title="Le champ communication fait 53 caractères et coupe au-delà. Un numéro de note a pu être perdu."
              >
                <AlertTriangle className="h-3 w-3" /> communication coupée
              </span>
            )}
          </div>

          {/*
            Le BÉNÉFICIAIRE d'abord, et en évidence.

            C'est ce que l'administration cherche en premier sur une dépense, et
            c'est ce qui manquait : l'écran n'affichait que la communication,
            tronquée à 53 caractères, sans dire à qui l'argent était parti.
            Les 1 758 virements sortants du fichier d'un an portent tous un nom
            et un IBAN de bénéficiaire dans l'enregistrement CODA 23 — ils
            n'étaient simplement jamais écrits.
          */}
          {/*
            Le bénéficiaire, et ce que la base sait de son compte. Quatre états
            distincts, parce qu'ils n'appellent pas le même geste : le compte
            désigne une famille, il en désigne plusieurs, il n'en désigne
            aucune, ou le relevé ne l'a pas donné du tout. Confondre le dernier
            avec les autres fait chercher un propriétaire qui n'a jamais été
            écrit.
          */}
          <p className="mt-1 truncate text-sm font-medium text-gray-900">
            {compte.etat === 'reconnu' ? (
              <>
                {compte.compte!.famille}
                <span className="ml-1 rounded bg-emerald-100 px-1 py-0.5 text-[10px] font-medium text-emerald-900">
                  compte reconnu
                </span>
              </>
            ) : depense.counterpartyName ? (
              depense.counterpartyName
            ) : compte.etat === 'absent' ? (
              <span className="font-normal italic text-gray-400">
                le relevé n'a donné ni nom ni compte
              </span>
            ) : (
              <span className="font-normal italic text-gray-400">bénéficiaire inconnu</span>
            )}
            {compte.etat === 'partage' && (
              <span className="ml-1 rounded bg-amber-100 px-1 py-0.5 text-[10px] font-medium text-amber-900">
                compte partagé
              </span>
            )}
          </p>

          <p className="truncate text-sm text-gray-700">
            {depense.communicationRaw || <span className="italic text-gray-400">sans communication</span>}
          </p>

          {(compte.ibanLisible || depense.notesPlacees) && (
            <p className="mt-0.5 truncate text-xs text-gray-500">
              {compte.ibanLisible && <span className="font-mono">{compte.ibanLisible}</span>}
              {compte.ibanLisible && compte.etat === 'reconnu' && (
                <span className="text-emerald-700">
                  {' · '}{compte.compte!.versements} versement
                  {compte.compte!.versements > 1 ? 's' : ''} reçu
                  {compte.compte!.versements > 1 ? 's' : ''} de ce compte
                </span>
              )}
              {compte.ibanLisible && depense.notesPlacees && ' · '}
              {depense.notesPlacees && <>Déjà placé sur : {depense.notesPlacees}</>}
            </p>
          )}
        </div>
      </button>

      {ouverte && (
        <>
          {/* La phrase entière, une fois dépliée : elle dit POURQUOI on sait,
              ou pourquoi on ne sait pas. */}
          <p className={clsx(
            'border-t border-gray-100 px-4 py-2 text-xs',
            compte.etat === 'reconnu' ? 'text-emerald-800'
              : compte.etat === 'partage' ? 'text-amber-800' : 'text-gray-500',
          )}>
            {compte.phrase}
          </p>
          {jumeau && (
            <div className="border-t border-gray-100 px-4 py-3">
              <VirementLuDeuxFois
                sens="sortie"
                ligne={depense}
                jumeau={jumeau}
                executer={(action, message) => onAgir(message, action)}
              />
            </div>
          )}
          <DetailDeDepense depense={depense} compte={compte} onAgir={onAgir} />
        </>
      )}
    </li>
  );
}

function DetailDeDepense({
  depense, compte, onAgir,
}: {
  depense: DepenseAPlacer;
  compte: LectureDuCompte;
  onAgir: (quoi: string, action: () => Promise<void>) => Promise<void>;
}) {
  const [placements, setPlacements] = useState<PlacementExistant[]>([]);
  const [recherche, setRecherche] = useState('');
  const [resultats, setResultats] = useState<NoteARembourser[]>([]);
  const [cherche, setCherche] = useState(false);

  useEffect(() => {
    /*
      Une première recherche préremplie avec ce que la communication cite :
      dans les 24 cas sur 45 qui citent les deux, le numéro est là, et le taper
      à la main serait une corvée inutile.

      ## Lue avec les fonctions du rapprochement, et rien d'autre

      Ce préremplissage lisait `refunds.legacy_invoice_ref` — une colonne ÉCRITE
      à l'import — et une expression régulière à lui. Il était donc le troisième
      lecteur de références de l'écran, et le seul à pouvoir proposer ce que les
      deux autres ne connaissaient pas.

      Signalé le 20 septembre 2026 : le champ s'ouvrait sur `CDV-CDV-2506002`,
      une référence fabriquée par un défaut de lecture (voir `TAGS_EXCLUS`) pour
      une communication qui disait `CDV-2506002-00072`. Elle ne désignait
      aucune facture et ne le pouvait pas.

      `referencesDuVirement` est la lecture de tout le reste du panneau. Quand
      elle ne reconnaît rien, le champ reste VIDE : c'est plus honnête qu'un
      numéro inventé, que l'on relance sans y penser.
    */
    const refs = referencesDuVirement(depense.communicationRaw, depense.freeText);
    const cite = refs.notes[0]?.numero
      ?? refs.factures.find((f) => f.numeroComplet)?.numeroComplet
      ?? '';
    setRecherche(cite);
    void loadPlacements(depense.id).then(setPlacements).catch(() => setPlacements([]));
  }, [depense.id, depense.communicationRaw, depense.freeText]);

  const chercher = useCallback(async (terme: string) => {
    setCherche(true);
    try {
      setResultats(await chercherNotesARembourser(terme));
    } catch {
      setResultats([]);
    } finally {
      setCherche(false);
    }
  }, []);

  return (
    <div className="space-y-4 border-t border-gray-100 bg-gray-50 px-4 py-4">
      {/*
        La LECTURE d'abord : ce que le virement cite, ce que ces notes valent,
        et le compte de la famille. La recherche à la main reste en dessous —
        elle sert au cas rare du virement qu'aucune annotation ne désigne.
      */}
      <NotesCitees depense={depense} compte={compte} agir={onAgir} />

      {depense.freeText && depense.freeText !== depense.communicationRaw && (
        <div>
          <p className="text-xs font-medium uppercase tracking-wide text-gray-500">
            Libellé complet du relevé
          </p>
          <p className="mt-1 whitespace-pre-wrap break-words text-sm text-gray-700">
            {depense.freeText}
          </p>
        </div>
      )}

      {placements.length > 0 && (
        <div>
          <p className="text-xs font-medium uppercase tracking-wide text-gray-500">Déjà placé</p>
          <ul className="mt-1 space-y-1">
            {placements.map((p) => (
              <li key={p.id} className="flex items-center justify-between gap-3 text-sm">
                <span className="text-gray-700">
                  <span className="tabular-nums font-medium">{euros(p.amountCents)}</span>
                  {' sur '}
                  {p.creditNoteNumber ?? (p.target === 'out_of_scope' ? 'hors périmètre familles' : 'une facture')}
                  {p.method === 'manual' && <span className="ml-1 text-xs text-gray-400">(à la main)</span>}
                </span>
                <button
                  onClick={() => onAgir(
                    'Placement défait — le virement revient dans la file',
                    () => defairePlacement(p.id),
                  )}
                  className="inline-flex items-center gap-1 text-xs text-gray-500 hover:text-red-700"
                  title="Défaire ne rend pas l'argent : le virement est parti. Cela dit seulement qu'on s'était trompé de pièce."
                >
                  <RotateCcw className="h-3 w-3" /> Défaire
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {depense.unallocatedCents > 0 && (
        <>
          <div>
            <p className="text-xs font-medium uppercase tracking-wide text-gray-500">
              Relier à une note de crédit
            </p>
            <div className="mt-1 flex gap-2">
              <input
                value={recherche}
                onChange={(e) => setRecherche(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') void chercher(recherche); }}
                placeholder="Numéro de note (NC-26-00417) ou de facture (CDV-MC-2601563)"
                className="flex-1 rounded-md border border-gray-300 px-3 py-1.5 text-sm"
              />
              <button
                onClick={() => void chercher(recherche)}
                disabled={recherche.trim().length < 2 || cherche}
                className="inline-flex items-center gap-1.5 rounded-md bg-indigo-600 px-3 py-1.5 text-sm font-medium text-white disabled:opacity-50"
              >
                {cherche ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
                Chercher
              </button>
            </div>
            <p className="mt-1 text-xs text-gray-500">
              Les notes « à rendre » — celles derrière lesquelles un encaissement dépasse
              le net de la facture — viennent en premier. Les notes « déduites » restent
              visibles mais n'appellent normalement aucun virement : elles ont réduit une
              facture que la famille n'avait pas payée. Rien n'est caché, parce que le
              rapprochement des paiements n'est pas terminé et que la moitié des notes déjà
              remboursées portent sur une facture que la comptabilité croit non réglée.
            </p>
          </div>

          {resultats.length > 0 && (
            <ul className="space-y-1">
              {resultats.map((n) => {
                /*
                  Le montant propose suit l'ARGENT, pas le montant de la note.
                  Une note de 92,00 EUR derriere laquelle seuls 30,00 EUR ont
                  ete recus ne propose que 30,00 EUR. Quand rien n'est prouve —
                  une note qui a simplement reduit une facture impayee — on
                  retombe sur le reste du virement, parce que le rapprochement
                  entrant n'est pas termine et que l'humain peut savoir ce que
                  la base ignore encore.
                */
                const propose = Math.min(
                  depense.unallocatedCents,
                  n.aRendreCents > 0 ? n.aRendreCents : n.refundableCents,
                );
                const libelle = LIBELLES_DE_DESTINEE[n.destinee];
                const avertissement = avertissementAvantRemboursement({
                  amountCents: n.amountCents,
                  refundedCents: n.refundedCents,
                  cashAdosseCents: n.cashAdosseCents,
                });
                return (
                  <li
                    key={n.id}
                    className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-gray-200 bg-white px-3 py-2 text-sm"
                  >
                    <div className="min-w-0">
                      <span className="font-medium text-gray-900">{n.number}</span>
                      <span className="ml-2 tabular-nums text-gray-700">{euros(n.amountCents)}</span>
                      <span
                        className={
                          n.destinee === 'a_rendre'
                            ? 'ml-2 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800'
                            : n.destinee === 'deduite'
                              ? 'ml-2 rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600'
                              : 'ml-2 rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-medium text-emerald-800'
                        }
                        title={libelle.explication}
                      >
                        {libelle.label}
                      </span>
                      {n.aRendreCents > 0 && n.aRendreCents !== n.amountCents && (
                        <span className="ml-2 text-xs text-amber-700">
                          dont {euros(n.aRendreCents)} adossés à un encaissement
                        </span>
                      )}
                      {n.refundedCents > 0 && (
                        <span className="ml-2 text-xs text-amber-700">
                          déjà rendu {euros(n.refundedCents)}
                        </span>
                      )}
                      <p className="text-xs text-gray-500">
                        {n.invoiceNumber ?? 'sans facture'}
                        {n.factureEncaisseCents !== null && <> · encaissé {euros(n.factureEncaisseCents)}</>}
                        {!!n.factureAncienCircuitCents && (
                          <> · ancien circuit {euros(n.factureAncienCircuitCents)}</>
                        )}
                      </p>
                      {avertissement && (
                        <p className="mt-1 flex items-start gap-1 text-xs text-gray-600">
                          <AlertTriangle className="mt-0.5 h-3 w-3 shrink-0 text-gray-400" />
                          <span>{avertissement}</span>
                        </p>
                      )}
                    </div>
                    <button
                      onClick={() => onAgir(
                        `${euros(propose)} placés sur ${n.number}`,
                        () => placerRemboursement(
                          depense.id,
                          [{
                            target: 'credit_note',
                            amountCents: propose,
                            creditNoteNumber: n.number,
                            creditNoteId: n.id,
                          }],
                          { userId: n.userId },
                        ),
                      )}
                      className="inline-flex shrink-0 items-center gap-1.5 rounded-md border border-indigo-600 px-3 py-1 text-sm font-medium text-indigo-700 hover:bg-indigo-50"
                    >
                      <Link2 className="h-4 w-4" /> Placer {euros(propose)}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}

          {recherche.trim().length >= 2 && !cherche && resultats.length === 0 && (
            <p className="text-sm text-gray-500">
              Aucune note remboursable ne correspond. Une note entièrement remboursée
              n'apparaît pas.
            </p>
          )}

          <div className="border-t border-gray-200 pt-3">
            <button
              onClick={() => onAgir(
                'Classé hors périmètre familles',
                () => classerHorsPerimetre(depense.id),
              )}
              className="inline-flex items-center gap-1.5 text-sm text-gray-600 hover:text-gray-900"
              title="Volontariat, frais de déplacement, salaire, fournisseur. Le mouvement reste lu et compté, mais n'est rattaché à aucune famille."
            >
              <Ban className="h-4 w-4" /> Classer hors périmètre familles
            </button>
          </div>
        </>
      )}
    </div>
  );
}
